import React, { useMemo } from 'react'
import { useTranslation, Trans, TFunction } from 'react-i18next'
import styles from '../styles/profile-header.module.scss'
import LocalImage from 'components/generic/LocalImage'
import wtnWidget from 'images/onboarding-tour/wtn_widget_tour.png'
import { PlayerProfileTourSteps, TourStep, TourStepKey } from 'constants/guided-tours'
import { TABLET_BREAKPOINT } from 'constants/global'
import { createRelPath } from 'constants/global'
import { ConstantQueryParamValues, QueryParamNames } from 'types/global'

function StepContent({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <article className={styles.stepContentContainer}>
      <header className={styles.stepContentTitle}>{title}</header>
      {children}
    </article>
  )
}

export const getOnboardingSteps = (tGuidedTour: TFunction<'strGuidedTour'>): TourStep[] => [
  {
    name: PlayerProfileTourSteps.PROFILE_OVERVIEW_TAB,
    joyrideStep: {
      target: '.overview-tab',
      content: (
        <StepContent title={tGuidedTour('overview.title')}>
          <p>{tGuidedTour('overview.content')}</p>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        placement: 'right',
        disableAnimation: true,
      },
    },
    joyrideOptions: {
      scrollOffset: 200,
    },
  },
  {
    name: PlayerProfileTourSteps.PROFILE_RESULTS_TAB,
    joyrideStep: {
      target: '.results-tab',
      content: (
        <StepContent title={tGuidedTour('results.title')}>
          <p>{tGuidedTour('results.content')}</p>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        disableAnimation: true,
        placement: 'right',
      },
    },
    joyrideOptions: {
      scrollOffset: 200,
    },
  },
  {
    name: PlayerProfileTourSteps.PROFILE_H2H_TAB,
    joyrideStep: {
      target: '.head-to-head-tab',
      content: (
        <StepContent title={tGuidedTour('headToHead.title')}>
          <p>{tGuidedTour('headToHead.content')}</p>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        disableAnimation: true,
        placement: 'right',
      },
    },
    joyrideOptions: {
      scrollOffset: 200,
    },
  },
  {
    name: PlayerProfileTourSteps.PROFILE_WTN_WIDGET,
    joyrideStep: {
      target: '.wtn-definition-wrapper',
      content: (
        <StepContent title={tGuidedTour('yourNumber.title')}>
          <div className={styles.widgetImageWrapper}>
            <LocalImage src={wtnWidget} alt='WTN Scale' className={styles.widgetImage} />
          </div>
          <p>{tGuidedTour('yourNumber.content')}</p>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        disableAnimation: true,
        placement: 'top',
      },
    },
    joyrideOptions: {
      scrollOffset: 20,
    },
  },
  {
    name: PlayerProfileTourSteps.PROFILE_ADD_TO_WATCHLIST,
    joyrideStep: {
      target: '.profile-watchlist',
      content: (
        <StepContent title={tGuidedTour('watchlist.title')}>
          <p>{tGuidedTour('watchlist.content')}</p>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        disableAnimation: true,
        placement: 'right',
      },
    },
    joyrideOptions: {
      scrollOffset: 200,
    },
  },
]

export const getSelfSubmitSteps = (tGuidedTour: TFunction<'strGuidedTour'>): TourStep[] => [
  {
    name: PlayerProfileTourSteps.PROFILE_MY_MATCHES_TAB,
    joyrideStep: {
      target: '.my-matches-tab',
      content: (
        <StepContent title={tGuidedTour('myMatches.title')}>
          <Trans ns='strGuidedTour' i18nKey='myMatches.content'>
            <p>
              <b>NEW!</b> Players can now post scores for the matches played outside of tournaments.
            </p>
            <a
              href={createRelPath('PLAYER_PROFILE', {
                queryParams: {
                  [QueryParamNames.PROFILE_OWN_PROFILE_FLAG]: 'true',
                  section: ConstantQueryParamValues.PROFILE_SOCIAL_MATCHES_RESULTS_SUB_SECTION,
                },
              })}
            >
              Visit your profile to begin posting scores.
            </a>
            <small>
              Note that players will only be able to submit a result against an opponent who holds
              an <b>ITF World Tennis Number</b> account, however this does exclude IPIN Juniors and
              Professionals.
            </small>
          </Trans>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        disableAnimation: true,
        placement: 'right',
      },
    },
    joyrideOptions: {
      scrollOffset: 200,
    },
  },
  {
    name: PlayerProfileTourSteps.PROFILE_COMPETITIONS_TAB,
    joyrideStep: {
      target: '.competitions-tab',
      content: (
        <StepContent title={tGuidedTour('competitions.title')}>
          <Trans ns='strGuidedTour' i18nKey='competitions.content'>
            <p>
              Under the ‘Competitions’ feature, all competition match results that have been
              submitted by a National or Regional Association, or the ITF, will be shown here.
            </p>
            <a
              href={createRelPath('PLAYER_PROFILE', {
                queryParams: {
                  [QueryParamNames.PROFILE_OWN_PROFILE_FLAG]: 'true',
                  section: ConstantQueryParamValues.PROFILE_COMPETITION_RESULTS_SUB_SECTION,
                },
              })}
            >
              Visit your profile to view your competition match results.
            </a>
            <small>Note that players will be unable to submit tournament match results here.</small>
          </Trans>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        disableAnimation: true,
        placement: 'right',
      },
    },
    joyrideOptions: {
      scrollOffset: 200,
    },
  },
]

export const getSelfSubmitStepsOwnProfile = (
  tGuidedTour: TFunction<'strGuidedTour'>
): TourStep[] => [
  {
    name: PlayerProfileTourSteps.PROFILE_MY_MATCHES_TAB,
    joyrideStep: {
      target: '.my-matches-tab',
      content: (
        <StepContent title={tGuidedTour('myMatches.title')}>
          <Trans ns='strGuidedTour' i18nKey='myMatches.contentOwnProfile'>
            <p>
              <b>NEW!</b> You can now post scores for the matches you played outside of tournaments.
            </p>
            <small>
              Note that you will only be able to submit a result against a player who holds an{' '}
              <b>ITF World Tennis Number</b> account, however this does exclude IPIN Juniors and
              Professionals.
            </small>
          </Trans>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        disableAnimation: true,
        placement: 'right',
      },
    },
    joyrideOptions: {
      scrollOffset: 200,
    },
  },
  {
    name: PlayerProfileTourSteps.PROFILE_COMPETITIONS_TAB,
    joyrideStep: {
      target: '.competitions-tab',
      content: (
        <StepContent title={tGuidedTour('competitions.title')}>
          <Trans ns='strGuidedTour' i18nKey='competitions.contentOwnProfile'>
            <p>
              All competition match results that have been submitted by your National or Regional
              Association, or the ITF, will appear in this tab.
            </p>
            <small>Note that you will not be able to submit scores here.</small>
          </Trans>
        </StepContent>
      ),
      disableBeacon: true,
      offset: 0,
      floaterProps: {
        disableAnimation: true,
        placement: 'right',
      },
    },
    joyrideOptions: {
      scrollOffset: 200,
    },
  },
]

export const useOnboardingSteps = () => {
  const { t: tGuidedTour } = useTranslation('strGuidedTour')

  const onboardingSteps = useMemo(() => getOnboardingSteps(tGuidedTour), [tGuidedTour])
  const selfSubmitSteps = useMemo(() => getSelfSubmitSteps(tGuidedTour), [tGuidedTour])
  const selfSubmitStepsOwnProfile = useMemo(
    () => getSelfSubmitStepsOwnProfile(tGuidedTour),
    [tGuidedTour]
  )

  return {
    onboardingSteps,
    selfSubmitSteps,
    selfSubmitStepsOwnProfile,
  }
}

export const getRuntimeStyles = (stepName: TourStepKey, windowWidth: number) => {
  let styles

  switch (stepName) {
    case PlayerProfileTourSteps.PROFILE_OVERVIEW_TAB:
      break
    case PlayerProfileTourSteps.PROFILE_RESULTS_TAB:
      styles = { overlay: { marginTop: '-1px' }, spotlight: { borderRadius: '0px' } }
      break
    case PlayerProfileTourSteps.PROFILE_H2H_TAB:
      styles = { overlay: { marginTop: '-1px' }, spotlight: { borderRadius: '0px' } }
      break
    case PlayerProfileTourSteps.PROFILE_WTN_WIDGET:
      if (windowWidth <= TABLET_BREAKPOINT) {
        styles = { spotlight: { borderRadius: '20px' } }
      } else {
        styles = { spotlight: { borderRadius: '40px' } }
      }
      break
    case PlayerProfileTourSteps.PROFILE_ADD_TO_WATCHLIST:
      styles = {
        spotlight: { borderRadius: '0px', paddingLeft: '10px', paddingRight: '10px' },
      }
      break
    default:
      styles = {}
  }

  return styles
}
